import React, { useEffect, useState } from 'react';
import { apiGet, apiPost } from '../utils/api';
import { BTN_XS } from '../constants/buttons';
import { FORM_INPUT_FIELD } from '../constants/inputs';
import { ROLE_LABELS } from '../constants/statuses';
import useGrantableRoles from '../hooks/useGrantableRoles';
import useSubmitGuard from '../hooks/useSubmitGuard';
import { useLatestRequest } from '../hooks/useSearchRequest';
import { friendlyErrorMessage } from './ErrorMessage';
import UserAutocomplete from './UserAutocomplete';
import AddPersonForm from './AddPersonForm';

/**
 * THE ONE "+ Add person" BOX (owner, 2026-11-03: *"lets build it!"*, from the
 * single-box mock, after *"why do we have two tabs … if their name or email
 * address doesnt start comming up then we offer to add"*).
 *
 * One search box for a KNOWN department — a department card's (Settings →
 * Departments) or the one the caller stands in (Settings → Users):
 *  - people the caller works with appear as they type
 *    (`GET /api/departments/:id/addable-people`, exactly who
 *    `POST /api/departments/:id/members` will seat); pick one, Add;
 *  - the dropdown's LAST row is always "+ Invite “<typed>”…", which opens
 *    `AddPersonForm` for this department with the typed text already in the
 *    Name box (or the Email box, when it looks like an address). No tab, no
 *    choosing up front whether someone is "new".
 *
 * 🔒 Typing a stranger's email shows the invite row, never a match: the list is
 * people the caller already shares a department with, so it reveals no account.
 *
 * 🔑 THE PAGE DECIDES WHO SEES WHICH ACTION (`canAddExisting`, `canSendInvite`,
 * `canAddLater`), each mirroring its server route, as `AddPersonForm` does.
 * With none of the invite actions the row is not offered.
 *
 * `subDepartments` (optional) adds the department card's Sub Dept picker.
 * `onDone(message, tone)` fires after ANY successful add, so the caller can
 * refresh whatever list it shows.
 */
const EMAIL_LIKE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const roleLabel = (role) => ROLE_LABELS[role] || role;

export default function AddPersonPanel({
  dept,
  isGlobalAdmin,
  activeDepartment,
  canAddExisting,
  canSendInvite,
  canAddLater,
  subDepartments = null,
  excludeUserIds = [],
  onClose,
  onDone,
}) {
  const deptId = Number(dept?.id) || null;

  // ── People the caller works with, fetched once per department ────────────
  const [people, setPeople] = useState([]);
  const begin = useLatestRequest();
  useEffect(() => {
    const isCurrent = begin();
    setPeople([]);
    if (!deptId || !canAddExisting) return;
    (async () => {
      try {
        const res = await apiGet(`/api/departments/${deptId}/addable-people`);
        const data = res.ok ? await res.json() : [];
        if (!isCurrent()) return;
        setPeople((Array.isArray(data) ? data : []).map((u) => ({
          id: u.id,
          display_name: u.display_name,
          status: u.status,
          hint: (u.shared_departments || []).join(', '),
        })));
      } catch {
        if (isCurrent()) setPeople([]);
      }
    })();
  }, [deptId, canAddExisting, begin]);

  const grantable = useGrantableRoles(deptId);
  const roles = grantable.length > 0 ? grantable : ['user'];

  const [name, setName] = useState('');
  const [userId, setUserId] = useState(null);
  const [role, setRole] = useState('user');
  const [subDeptId, setSubDeptId] = useState('');
  const [error, setError] = useState('');
  const [invite, setInvite] = useState(null); // { name, email } once "+ Invite" is picked
  const { guard, busy } = useSubmitGuard();
  const effectiveRole = roles.includes(role) ? role : (roles.includes('user') ? 'user' : roles[0]);
  const offerInvite = canSendInvite || canAddLater;

  const handleAdd = () => {
    if (!userId || !deptId) return;
    guard(async () => {
      try {
        setError('');
        const res = await apiPost(`/api/departments/${deptId}/members`, {
          user_id: userId,
          role: effectiveRole,
          sub_department_id: subDeptId ? parseInt(subDeptId, 10) : null,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to add member');
        setPeople((prev) => prev.filter((u) => u.id !== userId));
        window.dispatchEvent(new Event('sfx-role-change'));
        const added = name.trim() || data.display_name;
        setName(''); setUserId(null); setSubDeptId('');
        onDone(`${added} added to ${dept.name}`, 'success');
      } catch (err) {
        setError(friendlyErrorMessage(err));
      }
    });
  };

  if (invite) {
    return (
      <div data-testid={`add-person-panel-${deptId}`}>
        <AddPersonForm
          key={`${invite.name}|${invite.email}`}
          isGlobalAdmin={isGlobalAdmin}
          activeDepartment={activeDepartment}
          fixedDepartment={dept}
          initialName={invite.name}
          initialEmail={invite.email}
          canSendInvite={canSendInvite}
          canAddLater={canAddLater}
          onClose={() => setInvite(null)}
          onDone={(message, tone) => { setInvite(null); onDone(message, tone); }}
        />
      </div>
    );
  }

  const hasSub = Array.isArray(subDepartments);
  return (
    <div
      className={`mb-3 p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded flex flex-col gap-2 md:grid md:items-center md:gap-3 ${hasSub ? 'md:grid-cols-[2fr_1.5fr_1.5fr_auto]' : 'md:grid-cols-[3fr_1.5fr_auto]'}`}
      data-testid={`add-person-panel-${deptId}`}
    >
      <div data-testid="add-person-search">
        <UserAutocomplete
          value={name}
          onChange={(displayName, id) => { setName(displayName); setUserId(id || null); }}
          placeholder="Type a name or email…"
          ariaLabel="Person to add"
          options={canAddExisting ? people : []}
          excludeUserIds={excludeUserIds}
          onCreateNew={offerInvite ? (typed) => setInvite(
            EMAIL_LIKE.test(typed) ? { name: '', email: typed } : { name: typed, email: '' },
          ) : null}
        />
      </div>
      {hasSub && (
        <select value={subDeptId} onChange={(e) => setSubDeptId(e.target.value)}
          className={FORM_INPUT_FIELD} aria-label="Sub department" data-testid="add-person-sub-dept">
          <option value="">&mdash; No sub department &mdash;</option>
          {subDepartments.map((sd) => <option key={sd.id} value={sd.id}>{sd.name}</option>)}
        </select>
      )}
      {roles.length === 1 ? (
        <p className="px-1 py-2 text-sm text-gray-500 dark:text-gray-400" data-testid="add-person-panel-role">{roleLabel(roles[0])}</p>
      ) : (
        <select value={effectiveRole} onChange={(e) => setRole(e.target.value)}
          className={FORM_INPUT_FIELD} aria-label="Access rights" data-testid="add-person-panel-role">
          {roles.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
        </select>
      )}
      <div className="flex gap-1">
        <button type="button" onClick={handleAdd} disabled={busy || !userId}
          title={!userId ? 'Pick someone from the list, or choose “+ Invite” for someone new' : ''}
          className={`${BTN_XS.confirm} flex-1 md:flex-none min-h-[44px] md:min-h-0 inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed`}
          data-testid="add-person-panel-add">
          {busy ? '...' : 'Add'}
        </button>
        <button type="button" onClick={onClose}
          className={`${BTN_XS.secondary} flex-1 md:flex-none min-h-[44px] md:min-h-0 inline-flex items-center justify-center`}
          data-testid="add-person-panel-cancel">
          Cancel
        </button>
      </div>
      {error && <p role="alert" className="md:col-span-full text-xs text-red-700 dark:text-red-300">{error}</p>}
    </div>
  );
}
